import { z } from 'zod';
import { ARRANGEMENT_STATUSES } from './arrangement.schema';
import { objectIdSchema } from './common.schema';
import { TEMPLATE_DECISION_CLASSES, TEMPLATE_READINESS_STATES, templateSetKeySchema } from './templateSet.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §2] ONE TEMPLATE — ONE ORDER IN LAST YEAR'S BOOKLET, AS THE EXTRACTION READ IT.
 *
 *  ⚠️ NO OFFICER NAMES, EVER. The extraction removed names and phone numbers; the import's scrubber
 *  drops any that survived and removes the honorific a removed name left behind. An in-charge is a
 *  ROLE and, where the booklet said, the unit expected to supply one — never a person.
 *
 *  ⚠️ AN IN-CHARGE IS ONE ENTRY PER POSITION. "Inspector in-charge × 3" is three entries, so no
 *  in-charge shape carries a number (FR-5.2.6b). `continuesFrom` names the duty point whose in-charge
 *  this same person also is — the booklet's merged cell, which the extraction marked `shared`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export const TEMPLATE_PLACE_ISSUES = ['MISSING', 'DAMAGED', 'TOO_LONG'] as const;
export type TemplatePlaceIssue = (typeof TEMPLATE_PLACE_ISSUES)[number];

export const templateForceRowSchema = z.object({
  /** The booklet's column heading, as printed — `THG`, `U/A Men`. */
  column: z.string(),
  n: z.number().int().min(0),
  /** The force source's code as printed — `ND`, `SC` — or `null` where the booklet names none. */
  source: z.string().nullable(),
});

export const templateInChargeSchema = z.object({
  /** The booklet's role words — `Inspector in-charge`. */
  role: z.string(),
  /** The unit text the booklet gives for this position, scrubbed; `null` where it gives none. */
  unitText: z.string().nullable(),
  /** The unit codes recognised in `unitText`, each a `SOURCE` decision. */
  unitCodes: z.array(z.string()),
  /** The key of the duty point whose in-charge this same officer also is, or `null`. */
  continuesFrom: z.string().nullable(),
});

export const templateDutyPointSchema = z.object({
  /** Unique within the template — `g2.d4`. The booklet's own `sl` repeats, so it is not the key. */
  key: z.string(),
  sl: z.string().nullable(),
  place: z.string().nullable(),
  /** Why the place cannot be used as read — a `DUTY_POINT` decision — or `null`. */
  placeIssue: z.enum(TEMPLATE_PLACE_ISSUES).nullable(),
  reportingPlace: z.string().nullable(),
  reportTo: z.string().nullable(),
  remarks: z.string().nullable(),
  notes: z.array(z.string()),
  page: z.number().int().nullable(),
  force: z.array(templateForceRowSchema),
  inCharge: z.array(templateInChargeSchema),
  /** Cells the extraction could not read as force — dropped, and stated. */
  unparsedCells: z.array(z.string()),
  /** The booklet calls this duty point round the clock: flagged, never built. */
  roundTheClock: z.boolean(),
});

export const templateGroupSchema = z.object({
  zone: z.string().nullable(),
  zoneDescription: z.string().nullable(),
  sector: z.string().nullable(),
  sectorDescription: z.string().nullable(),
  /** Zone and sector supervision rows, as text — a role and a unit, never a person. */
  supervision: z.array(z.string()),
  dutyPoints: z.array(templateDutyPointSchema),
});

/** A printed total row against the rows the extraction read. A differing one is a `TOTALS` decision. */
export const templateCheckSchema = z.object({
  /** `p11:t1` — the `TOTALS` code. */
  id: z.string(),
  page: z.number().int().nullable(),
  comparable: z.boolean(),
  differs: z.boolean(),
  /**
   * The duty points the printed table covers, in booklet order — empty when the import could not
   * establish them, and then the printed figure cannot be reached by correcting rows (only "as read"
   * or "hold" is offered).
   */
  dutyPointKeys: z.array(z.string()),
  columns: z.array(
    z.object({
      column: z.string(),
      printed: z.number().int().nullable(),
      parsed: z.number().int(),
      /** People in this column the booklet's in-charge cells account for — never part of a force row. */
      inCharge: z.number().int().min(0),
    }),
  ),
});

export const templateReadinessSchema = z.object({
  state: z.enum(TEMPLATE_READINESS_STATES),
  /** In a sentence each; empty when READY. */
  reasons: z.array(z.string()),
});

/** An order this template produced, so the library and the plan can say so and link to it ([P7B §4e]). */
export const templateProducedSchema = z.object({
  arrangementId: objectIdSchema,
  reference: z.string(),
  paNumber: z.string().nullable(),
  status: z.enum(ARRANGEMENT_STATUSES),
  archivedAt: z.iso.datetime().nullable(),
  part: z.number().int().min(1),
  parts: z.number().int().min(1),
});

/**
 * ⚠️ [P7B.1] WHAT A TEMPLATE STILL WAITS ON — every open decision holding it back, in words.
 *
 * It EXPLAINS readiness and never decides it: `readiness.state` is `resolveTemplate`'s, and this is the
 * decisions page's own inventory read per template (`openDecisionsOf`), so a code here is a row on that
 * page under the same label. The two can differ at the edges, and the screen trusts the state: a
 * stored choice that stopped holding (an archived policy, a unit gone) blocks with no open code, and a
 * code on a duty point decided "leave it out" still waits here while the template is ready — exactly as
 * the set's own count of outstanding decisions has always counted it.
 *
 * `code: null` is a MARKER, never a decision: a policy waiting for its columns to be decided, or an
 * in-charge role waiting for its policy. Only those two classes can wait.
 */
export const templateWaitSchema = z.union([
  z.object({ class: z.enum(TEMPLATE_DECISION_CLASSES), code: z.string(), label: z.string() }),
  z.object({
    class: z.enum(TEMPLATE_DECISION_CLASSES).extract(['POLICY', 'IN_CHARGE_ROLE']),
    code: z.null(),
    label: z.string(),
  }),
]);

/**
 * [P7B.1] People per booklet column and source, summed over the duty points that will be BUILT — round
 * the clock is left out, so a template flagged whole, or holding nothing, carries none. What the review
 * site's force matrices show, and what tells an unusual column from a usual one.
 */
export const templateForceTotalSchema = z.object({
  /** The booklet's column heading, as printed. */
  column: z.string(),
  /** The `SOURCE` decision code — `TEMPLATE_SOURCE_NONE` where the booklet names none. */
  source: z.string(),
  n: z.number().int().min(1),
});

export const templateListItemSchema = z.object({
  id: objectIdSchema,
  key: z.string(),
  ordinal: z.number().int().min(1),
  part: z.string(),
  sectionKey: z.string(),
  sectionTitle: z.string(),
  divisionCode: z.string().nullable(),
  divisionName: z.string().nullable(),
  title: z.string(),
  pages: z.array(z.number().int()),
  dutyPointCount: z.number().int().min(0),
  /** The section's decided festival days, or its proposal while undecided. */
  dayKeys: z.array(z.string()),
  scheduleDecided: z.boolean(),
  readiness: templateReadinessSchema,
  /** [P7B.1] `[]` when nothing is open — a ready, round-the-clock or empty template. */
  waitsOn: z.array(templateWaitSchema),
  /** [P7B.1] `[]` for a template that builds nothing. */
  force: z.array(templateForceTotalSchema),
  produced: z.array(templateProducedSchema),
});

export const templateListSchema = z.object({
  items: z.array(templateListItemSchema),
  total: z.number().int().min(0),
});

export const arrangementTemplateSchema = templateListItemSchema.extend({
  info: z.array(z.string()),
  instructions: z.array(z.string()),
  notes: z.array(z.string()),
  supervision: z.array(z.string()),
  groups: z.array(templateGroupSchema),
  checks: z.array(templateCheckSchema),
  /** What the extraction or the import could not settle, in sentences — for a reviewer. */
  reviewNotes: z.array(z.string()),
});

export const templateParamsSchema = z.object({
  setKey: templateSetKeySchema,
  templateId: objectIdSchema,
});
